import { useState, type Dispatch, type SetStateAction } from "react";
import { useI18N } from "@src/language/language-context";
import { cancelInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/cancel-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface UseOtaBackupProcessPendingApprovalByOperatorProps {
  activeInstallationData: ActiveInstallationProcessPayload | null;
  setInstallAndActionBlock: Dispatch<
    SetStateAction<InstallAndActionBlock | null>
  >;
}

export const useOtaBackupProcessPendingApprovalByOperator = ({
  activeInstallationData,
  setInstallAndActionBlock,
}: UseOtaBackupProcessPendingApprovalByOperatorProps) => {
  const i18n = useI18N();
  const [isCancelRequestLoading, setIsCancelRequestLoading] = useState(false);

  const texts = {
    title: i18n("modules.otaBackupProcessPendingApprovalByOperator.title"),
    description: i18n(
      "modules.otaBackupProcessPendingApprovalByOperator.description",
    ),
    cancelRequestButtonText: i18n(
      "modules.otaBackupProcessPendingApprovalByOperator.cancelRequestButtonText",
    ),
    cancelSuccessMessage: i18n(
      "modules.otaBackupProcessPendingApprovalByOperator.cancelSuccessMessage",
    ),
    cancelFailureMessage: i18n(
      "modules.otaBackupProcessPendingApprovalByOperator.cancelFailureMessage",
    ),
  };

  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const handleCancelRequest = async () => {
    if (isCancelRequestLoading) {
      return;
    }

    const updateId = activeInstallationData?.update_id;
    if (!updateId) {
      return;
    }

    setIsCancelRequestLoading(true);

    try {
      const response = await cancelInstallationProcessUseCase(updateId);
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Cancel backup process response:",
          response,
          "for updateId:",
          updateId,
        );
      if (response.success) {
        setInstallAndActionBlock(null);
        successSnackbar(texts.cancelSuccessMessage, 5000);
        return;
      }

      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    } catch {
      errorSnackbar(texts.cancelFailureMessage, 5000);
      setIsCancelRequestLoading(false);
    }
  };

  return {
    texts,
    handleCancelRequest,
    isCancelRequestLoading,
  };
};
